import React, { useState, useEffect } from 'react';
import { VStack, View, Text, Heading, Box, Spacer } from 'native-base';

import { functions } from '@breakroom-nx/apis';
import { httpsCallable } from 'firebase/functions';
import {
  getAuth,
  signInAnonymously,
  updateProfile,
  browserLocalPersistence,
  setPersistence,
} from 'firebase/auth';

import { theme } from '@breakroom-nx/assets';
import { Shuffler, shuffle, getAvatarURL } from '@breakroom-nx/classes';
import { useAppDispatch, openModal } from '@breakroom-nx/redux';
import { Status } from '@breakroom-nx/helpers';
import { PrimaryButton, SecondaryButton, AnimatedShuffleView } from '../../';

export interface AnonymousSetupProps {
  onSuccess: () => void;
}

export function AnonymousSetup({ onSuccess }: AnonymousSetupProps) {
  const auth = getAuth();
  const dispatch = useAppDispatch();
  const createUser = httpsCallable(functions, 'user-createuser');
  const [creating, setCreating] = useState(false);
  const [shuffler, setShuffler] = useState<Shuffler | undefined>();
  const [showAnonymousAvatar, setShowAnonymousAvatar] = useState(false);

  useEffect(() => {
    setShuffler(shuffle());
  }, []);

  useEffect(() => {
    if (!showAnonymousAvatar) {
      const timeout = setTimeout(() => {
        setShuffler(shuffle());
      }, 500);
      return () => clearTimeout(timeout);
    } else {
      return;
    }
  }, [showAnonymousAvatar]);

  useEffect(() => {
    if (shuffler) {
      setShowAnonymousAvatar(true);
    }
  }, [shuffler]);

  const createAnonymousAccount = () => {
    setCreating(true);
    if (shuffler) {
      signInAnonymously(auth)
        .then(async (credential) => {
          const username = `${shuffler.adjective}-${shuffler.object.noun}`;
          const uid = credential.user.uid;

          await updateProfile(credential.user, {
            displayName: username,
          })
            .then(() => {
              // console.log(`[ANONYMOUS SETUP] Successfully updated the profile`);
            })
            .catch((err) => {
              // console.log(
              //   `[ANONYMOUS SETUP] Failed to update profile. Error: ${err}`
              // );
            });

          // console.log(
          //   `[ANONYMOUS SETUP] User Made: ${uid} with username: ${username}`
          // );

          const avatarURL = await getAvatarURL(
            `avatars/${shuffler.object.noun}.png`
          )
            .then((url) => {
              // console.log(`[ANONYMOUS SETUP] Got avatar URL: ${url}`);
              return url;
            })
            .catch((err) => {
              // console.log(
              //   `[ANONYMOUS SETUP] Failed to Get Avatar: [${err.code} | ${err.message}]`
              // );
              return undefined;
            });

          await createUser({
            username: username,
            theme: shuffler.color,
            avatar: avatarURL,
          })
            .then(async () => {
              await setPersistence(auth, browserLocalPersistence)
                .then(() => {
                  // console.log(`[ANONYMOUS SETUP] Browser Persistence Set`);
                  sessionStorage.setItem(
                    'authToken',
                    credential.user.refreshToken
                  );
                  onSuccess();
                  setCreating(false);
                })
                .catch((err) => {
                  // console.log(
                  //   `[ANONYMOUS SETUP] [Local Persistence] (Failed) ${err.code}: ${err.message}`
                  // );
                  sessionStorage.setItem(
                    'authToken',
                    credential.user.refreshToken
                  );
                  onSuccess();
                  setCreating(false);
                });

              // console.log(`[ANONYMOUS SETUP] Done Uploading User Data`);
            })
            .catch((err) => {
              // console.log(
              //   `[ANONYMOUS SETUP] User Data Upload Failed: ${err.code} ${err.message}`
              // );
            });
        })
        .catch((error) => {
          setCreating(false);
          const errorMessage = error.message;

          dispatch(
            openModal({
              modalType: 'Alert',
              modalProps: {
                status: Status.error,
                title: `There was an error creating an anonymous account`,
                description: errorMessage,
              },
            })
          );
        });
    }
  };

  return (
    <View
      height={'full'}
      width={'full'}
      left={0}
      top={0}
      justifyContent={'center'}
      mt={7}
    >
      <VStack>
        <VStack space={'5'}>
          <Heading
            fontFamily={'heading'}
            fontWeight={600}
            fontStyle={'normal'}
            textAlign={'left'}
            fontSize={28}
            lineHeight={30}
          >
            Let's genenerate an <Text color={'surface.500'}>anonymous</Text>{' '}
            profile for you...
          </Heading>

          <Spacer />
          <Spacer />

          <VStack space={5}>
            <Box
              rounded={'full'}
              alignSelf={'center'}
              bg={'transparent'}
              alignItems={'center'}
              justifyContent={'center'}
              p={'25px'}
              size={100}
            >
              {shuffler && (
                <AnimatedShuffleView
                  visible={showAnonymousAvatar}
                  color={shuffler?.color}
                  avatar={shuffler?.object.avatar}
                />
              )}
            </Box>
            <Heading
              fontFamily="heading"
              fontWeight={600}
              fontStyle="normal"
              textAlign={'center'}
            >
              Hey there,{'\n'}
              <Text color={shuffler?.color}>
                {`${shuffler?.adjective}-${shuffler?.object.noun}`}
              </Text>
              !
            </Heading>

            <PrimaryButton
              isLoading={creating}
              disabled={creating || shuffler === undefined}
              text={'Start chatting'}
              bg={shuffler?.color}
              onPress={() => {
                createAnonymousAccount();
              }}
            />
            <SecondaryButton
              disabled={creating}
              text={'Shuffle again!'}
              color={shuffler?.color}
              onPress={() => {
                setShowAnonymousAvatar(false);
              }}
            />
            <Text
              mt={5}
              textAlign={'center'}
              color={theme.colors.surface[500]}
              lineHeight={17}
            >
              Psst, don't worry - you can change your username later on if you
              choose to convert to a public account.
            </Text>
            <Text
              color={'surface.600'}
              textAlign={'center'}
              fontSize={15}
              pb={10}
            >
              By creating an account, you agree to the{' '}
              <a
                href={
                  'https://app.termly.io/policy-viewer/policy.html?policyUUID=71c165d8-a9d6-4991-9af6-371f16ba897a'
                }
              >
                <Text color={'primary.400'}>Terms and Conditions</Text>
              </a>{' '}
              and the{' '}
              <a
                href={
                  'https://app.termly.io/policy-viewer/policy.html?policyUUID=000f40f7-6bbe-4456-9758-32d4bde72439'
                }
              >
                <Text color={'primary.400'}>Privacy Policy</Text>
              </a>
            </Text>
          </VStack>
        </VStack>
      </VStack>
    </View>
  );
}

export default React.memo(AnonymousSetup);
